import { useIntl } from 'react-intl'
import { Card, Divider, Grid } from '@mui/material'
import { Add } from '@mui/icons-material'
import { CatalogueEntry } from 'models/catalogues'
import {
  formatCatalogueLabel,
  getFormattedCurrency,
  getFormattedDate,
  renderCatalogueOption,
} from 'lib/helpers/shared.helpers'
import { selectCatalogue, selectProductsList } from 'lib/helpers/selectors'
import { useAppSelector } from 'store/hooks'
import Button from 'components/Shared/Button/Button'
import { TaskAlt } from 'components/Shared/CustomIcons'
import SydDialog from 'components/Shared/SydDialog/SydDialog'
import { DropdownCard } from 'components/FormComponents/Dropdown/Dropdown'
import Form from 'components/FormComponents/IOComponents/IOCustomForm/IOCustomForm'
import AutocompleteField from 'components/FormComponents/IOComponents/AutocompleteField/AutoCompleteField'
import NumericField from 'components/FormComponents/IOComponents/NumericField/NumericField'
import TextField from 'components/FormComponents/IOComponents/TextField/TextField'
import { DisplayTotalDetails } from 'components/Invoices/DisplayTotal/DisplayTotal'
import ScrollSpy from 'components/Invoices/ScrollSpy/ScrollSpy'
import AddConceptForm from '../AddCreditNoteConceptContainer/AddCreditNoteConceptContainer'
import useAddCreditNoteContainer from './useAddCreditNoteContainer'
import './styles.scss'

export interface IAddCreditNoteContainerProps {
  workspaceId: number
  invoiceId: number
  cleanup: () => void
}

const AddCreditNoteContainer = ({
  workspaceId,
  invoiceId,
  cleanup,
}: IAddCreditNoteContainerProps) => {
  const intl = useIntl()

  const products = useAppSelector(selectProductsList)
  const monedas = useAppSelector(selectCatalogue('monedas'))
  const formasPago = useAppSelector(selectCatalogue('formasPago'))

  const {
    creditNoteSuccess,
    form,
    formValid,
    invoice,
    loading,
    metodosPago,
    taxes,
    usosCfdi,
    filterTipoRelacion,
    valid,
    addConcept,
    onAdd,
    onCancelCreditNote,
    onDownloadCreditNoteFiles,
    onSetExchangeRate,
    saveValueToStore,
    setValid,
  } = useAddCreditNoteContainer({
    workspaceId,
    invoiceId,
    cleanup,
  })

  return (
    <>
      <DropdownCard
        title="Factura Original"
        headerBackgroundColor="secondary"
        capitalize
        iconAlign="left"
        defaultExpanded
        elevation={3}>
        <Form onSubmit={() => true} onValidity={() => true}>
          <Grid container spacing={2}>
            <Grid item xs>
              <TextField
                name="fecha"
                label="Fecha"
                value={getFormattedDate(invoice?.createdAt)}
                InputProps={{ disableUnderline: true }}
                inputProps={{ readOnly: true }}
                fullWidth
                exclude
              />
            </Grid>
            <Grid item xs>
              <TextField
                name="serie"
                label="Serie"
                value={invoice?.serie}
                InputProps={{ disableUnderline: true }}
                inputProps={{ readOnly: true }}
                fullWidth
                exclude
              />
            </Grid>
            <Grid item xs>
              <TextField
                name="folio"
                label="Folio"
                value={invoice?.folio}
                InputProps={{ disableUnderline: true }}
                inputProps={{ readOnly: true }}
                fullWidth
                exclude
              />
            </Grid>
            <Grid item xs>
              <TextField
                name="razonSocial"
                label="Razón social"
                value={invoice?.emisor?.name}
                InputProps={{ disableUnderline: true }}
                inputProps={{ readOnly: true }}
                fullWidth
                exclude
              />
            </Grid>
            <Grid item xs>
              <TextField
                name="moneda"
                label="Moneda"
                value={formatCatalogueLabel(invoice?.currency)}
                InputProps={{ disableUnderline: true }}
                inputProps={{ readOnly: true }}
                fullWidth
                exclude
              />
            </Grid>
            <Grid item xs>
              <TextField
                name="total"
                label="Total"
                value={getFormattedCurrency(invoice?.total)}
                InputProps={{ disableUnderline: true }}
                inputProps={{ readOnly: true }}
                fullWidth
                exclude
              />
            </Grid>
          </Grid>
        </Form>
      </DropdownCard>
      <Card elevation={1} className="container-add-credit-note">
        <div className="scrollspy-container">
          <ScrollSpy
            items={[
              { id: 'section-1', label: '1', tooltip: 'CFDI' },
              { id: 'section-2', label: '2', tooltip: 'Conceptos' },
              {
                id: 'section-3',
                label: '3',
                tooltip: 'Documentos relacionados',
              },
              { id: 'section-4', label: '4', tooltip: 'Serie' },
            ]}
          />
        </div>
        <div className="form-container">
          <Form onSubmit={onAdd} onValidity={v => setValid(v)}>
            <div id="section-1" className="content">
              <h3 className="title">(1) CFDI</h3>
              <Divider className="separator" />
              <Grid container spacing={4}>
                <Grid item xs={7}>
                  <AutocompleteField
                    name="usoCfdi"
                    label="Uso de Comprobante"
                    catalogue={usosCfdi}
                    value={form?.usoCfdi}
                    onValue={(v: CatalogueEntry) =>
                      saveValueToStore('usoCfdi', v)
                    }
                    fullWidth
                    getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
                    getOptionSelected={(
                      option: CatalogueEntry,
                      value: CatalogueEntry
                    ) => option.idCatalogValues === value.idCatalogValues}
                    renderOption={(o: any) => renderCatalogueOption(o)}
                    required
                  />
                </Grid>
                <Grid item xs={4}></Grid>
                <Grid item xs={4}>
                  <AutocompleteField
                    name="moneda"
                    label="Moneda"
                    catalogue={monedas}
                    value={form?.moneda}
                    onValue={(v: CatalogueEntry) => onSetExchangeRate(v)}
                    fullWidth
                    getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
                    getOptionSelected={(
                      option: CatalogueEntry,
                      value: CatalogueEntry
                    ) => option.idCatalogValues === value.idCatalogValues}
                    renderOption={(o: any) => renderCatalogueOption(o)}
                    required
                  />
                </Grid>
                {!!form?.moneda && form.moneda?.value !== 'MXN' && (
                  <Grid item xs={4}>
                    <NumericField
                      name="tipoCambio"
                      label="Tipo de cambio"
                      fullWidth
                      value={form?.exchangeRate}
                      onValue={(v: any) => saveValueToStore('exchangeRate', v)}
                      format="exchangeRate"
                      required
                    />
                  </Grid>
                )}
                <Grid item xs={4}>
                  <AutocompleteField
                    name="metodoPago"
                    label="Método de pago"
                    catalogue={metodosPago}
                    value={form?.metodoPago}
                    onValue={(v: CatalogueEntry) =>
                      saveValueToStore('metodoPago', v)
                    }
                    fullWidth
                    getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
                    getOptionSelected={(
                      option: CatalogueEntry,
                      value: CatalogueEntry
                    ) => option.idCatalogValues === value.idCatalogValues}
                    renderOption={(o: any) => renderCatalogueOption(o)}
                    required
                  />
                </Grid>
                <Grid item xs={4}>
                  <AutocompleteField
                    name="formaPago"
                    label="Forma de pago"
                    catalogue={formasPago}
                    value={form?.formaPago}
                    onValue={(v: CatalogueEntry) =>
                      saveValueToStore('formaPago', v)
                    }
                    fullWidth
                    getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
                    getOptionSelected={(
                      option: CatalogueEntry,
                      value: CatalogueEntry
                    ) => option.idCatalogValues === value.idCatalogValues}
                    renderOption={(o: any) => renderCatalogueOption(o)}
                    required
                  />
                </Grid>
                <Grid item xs={12} />
                <Grid item xs={8}>
                  <p className="form-label">
                    Condiciones de pago - Información adicional
                  </p>
                  <TextField
                    variant="outlined"
                    name="condiciones"
                    value={form?.condiciones}
                    onValue={(v: string) => saveValueToStore('condiciones', v)}
                    inputProps={{ maxLength: 500 }}
                    multiline
                    minRows={4}
                    maxRows={4}
                    withCounter
                    fullWidth
                  />
                </Grid>
              </Grid>
            </div>
            <div id="section-2" className="content">
              <h3 className="title">(2) Conceptos</h3>
              <Divider className="separator" />
              <p className="form-label-alternative">
                Selecciona un concepto guardado anteriormente o agrega uno nuevo
              </p>
              <Grid
                className="section-add-item"
                container
                justifyContent="space-between">
                <Grid item xs={5}>
                  <AutocompleteField
                    name="conceptos"
                    placeholder="Buscar"
                    catalogue={products}
                    fullWidth
                    getOptionLabel={(o: any) => `${o.name}`}
                    onSelectValue={addConcept}
                    exclude
                  />
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<Add />}
                    onClick={() => addConcept()}>
                    Nuevo Concepto
                  </Button>
                </Grid>
              </Grid>
              {form?.conceptos.map((c, i) => (
                <AddConceptForm
                  id={i}
                  key={c.uuid}
                  title={`Concepto ${i + 1}`}
                  uuid={c.uuid}
                  clave={c.satKey}
                  unidad={c.claveUnitSat}
                  precio={c.price}
                  cantidad={c.cantidad}
                  descuento={c.descuento}
                  descripcion={c.description}
                  impuestos={c.impuestos}
                  importacion={c.importacion}
                  impuestosCat={taxes}
                  predial={c.predial}
                />
              ))}
              <div className="invoice-breakdown">
                <DisplayTotalDetails concepts={form?.conceptos} />
              </div>
            </div>
            <div id="section-3" className="content">
              <h3 className="title">(3) Documentos relacionados</h3>
              <Divider className="separator" />
              <Grid container spacing={4}>
                <Grid item xs={4}>
                  <TextField
                    name="uuid"
                    label="UUID"
                    value={form.uuid ? form.uuid : ''}
                    fullWidth
                    disabled
                    exclude
                  />
                </Grid>
                <Grid item xs={4}>
                  <AutocompleteField
                    name="relationType"
                    label="Tipo de relación"
                    placeholder="Buscar"
                    catalogue={filterTipoRelacion}
                    fullWidth
                    getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
                    renderOption={(o: any) => renderCatalogueOption(o)}
                    onSelectValue={(val: any) =>
                      saveValueToStore('tipoRelacion', val)
                    }
                    value={form?.tipoRelacion}
                    required
                  />
                </Grid>
              </Grid>
            </div>
            <div id="section-4" className="content">
              <h3 className="title">(4) Serie</h3>
              <Divider className="separator" />
              <Grid container spacing={4}>
                <Grid item xs={4}>
                  <TextField
                    name="serie"
                    label="Serie"
                    value={form?.serie}
                    disabled
                    fullWidth
                    exclude
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    name="folio"
                    label="Folio"
                    value={form?.folio}
                    fullWidth
                    disabled
                    exclude
                  />
                </Grid>
              </Grid>
            </div>
            <Divider className="separator" />
            <div className="actions">
              {process.env.REACT_APP_ENVIRONMENT === 'development' && (
                <Button
                  variant="outlined"
                  color="secondary"
                  activity={loading}
                  disabled={!valid}>
                  Vista previa
                </Button>
              )}
              <Button
                variant="contained"
                type="submit"
                color="secondary"
                activity={loading}
                disabled={!valid || !formValid}>
                Agregar
              </Button>
            </div>
          </Form>
        </div>
      </Card>

      <SydDialog
        open={creditNoteSuccess}
        icon={TaskAlt}
        hidePrimaryButton
        fullWidth
        onClose={() => onCancelCreditNote()}>
        <div className="success-dialog">
          <div className="title-dialog">
            <h1>
              {intl.formatMessage({ id: 'creditNote.succcess.diolog.title' })}
            </h1>
          </div>
          <div className="text-container" style={{ marginTop: '-10px' }}>
            <h1>
              {intl.formatMessage({ id: 'creditNote.succcess.diolog.message' })}
            </h1>
          </div>
          <div className="buttons-container">
          <Grid container spacing={1} justifyContent="center">
            <Grid item xs={6}>
              <Button
                variant="contained"
                color="secondary"
                className="lgButton"
                onClick={() => onDownloadCreditNoteFiles()}>
                {intl.formatMessage({ id: 'button.download' })}
              </Button>
            </Grid>
          </Grid>
        </div>
        </div>
      </SydDialog>
    </>
  )
}

export default AddCreditNoteContainer
